/*
    {
        image: "image",
        pdf: "pdf",
        title: "title of credential",
        organization: "organization of credential",
        details: "body of text which describes more detail about the certification",
        shortOrg: "acronym/initialism of organization",
        validationUrl: "URL to where users can verify the cert",
        validationCode: "verification code of the cert",
        order: 1,
        rows: 2,
        cols: 2
    }
*/

import awsCanWebp from '../assets/awscan.webp';
import awsCanPdf from '../assets/awscan.pdf';
import awsSapWebp from '../assets/awssap.webp';
import awsSapPdf from '../assets/awssap.pdf';
import awsDevopWebp from '../assets/awsdevop.webp';
import awsDevopPdf from '../assets/awsdevop.pdf';
import awsSysopWebp from '../assets/awssysop.webp';
import awsSysopPdf from '../assets/awssysop.pdf';
import ckadWebp from '../assets/ckad.webp';
import ckadPdf from '../assets/ckad.pdf';
import hctaWebp from '../assets/hcta.webp';
import hctaPdf from '../assets/hcta.pdf';
import hcvaWebp from '../assets/hcva.webp';
import hcvaPdf from '../assets/hcva.pdf';
import ufswdWebp from '../assets/ufswd.webp';
import ufswdPdf from '../assets/ufswd.pdf';
import ccnarsWebp from '../assets/ccnars.webp';
import ccnarsPdf from '../assets/ccnars.pdf';
import caplusWebp from '../assets/caplus.webp';
import caplusPdf from '../assets/caplus.pdf';
import canplusWebp from '../assets/canplus.webp';
import canplusPdf from '../assets/canplus.pdf';
import casplusWebp from '../assets/casplus.webp';
import casplusPdf from '../assets/casplus.pdf';
import mitxsixWebp from '../assets/mitx6.webp';
import mitxsixPdf from '../assets/mitx6.pdf';
import itilWebp from '../assets/itilfoundation.webp';
import itilPdf from '../assets/itilfoundation.pdf';
import gadWebp from '../assets/gad.webp';
import gadPdf from '../assets/gad.pdf';


const CREDENTIALS = [
    {
        image: awsCanWebp,
        pdf: awsCanPdf,
        title: "Certified Advanced Networking - Specialty",
        details: `### Technical Skills Learned/Refreshed
* VPC endpoints (gateway, interface)
* R53 (Private Hosted Zones)
* Direct Connect (VIFs, BGP, BFDs, etc)
* VPN (Site-to-Site with IKEv1,v2, IPSec for Cisco/Meraki Gateways, ECMP)
* BGP (Community Tags, Metric-based routing, AZs)
* Amazon Time Sync Service, CloudMap and various other networking services (for IDS, IPS)
### Context/Purpose
I achieved the AWS Advanced Networking Specialty certification while I was with Rackspace. My primary reason for pursuing this certification was to delve into the deeper networking-side of AWS as I'd felt I did not get to deal with it as much in my day to day duties and projects. In my career I'd implemented Site-to-Site VPN with AWS <-> On-prem datacenter/Meraki when I was with Pill Club/Favor. With Rackspace I had many networking projects implementing various networking schemes with VPC peering, VPC endpoints, transit gateways touching the different services but felt that I did not know as much as I wanted to. 
### Post Certification Retrospect
After a month of studying and refreshing concepts in my mind I'd learned a slurry of new skills and technologies and passed. Some of the more interesting things I'd learned dealt with implementing BGP with Direct Connect, VIFs and the eccentricities of such. I'd also learned about a few new tools I didn't know about for planning out the overall networking scheme (CIDR) of a greenfield deployment. Overall, I enjoyed studying for this certification, before I became a cloud architect I used to do more direct work with networking and this was a great chance to reconnect and bridge both worlds for me.`,
        organization: "AWS",
        validationUrl: "https://aws.amazon.com/verification",
        validationCode: "2K59KENB3JFEQQ5T",
        order: 4,
        rows: 2,
        cols: 2
    },
    {
        image: awsSapWebp,
        pdf: awsSapPdf,
        title: "Certified Solutions Architect - Professional",
        details: `### Technical Skills Learned/Refreshed
* AWS Networking (VPCs, VPC peering, transit gateway, VPNs, R53, flowlogs etc)
* AWS Security (KMS, CloudTrail, IAM etc)
* General Security Concepts (encryption at rest/in transit for common AWS Services)
* Account Architecture (Multi-Account, Cross Region)
* Elasticity (Auto-Scaling, Serverless, Managed)
* Common S3 Services and Design patterns (SNS, SQS, StepFunctions, Event-based /w event bus etc)

### Context/Purpose
The AWS Solutions Architect Professional certification was one of the key milestones in my journey to "formalize" the professional knowledge I had gained during my time at Rackspace. By then, I had held the Solutions Architect Associate certification for several years and had accumulated extensive experience working with AWS across a wide range of use cases. These included building CI/CD pipelines for both existing and new applications, implementing observability layers, developing serverless applications, deploying static websites, designing API integrations, creating custom tools, handling lift-and-shift migrations, and more. With this wealth of experience, I felt it was the right time to challenge myself with the Solutions Architect Professional certification. 
### Post Certification Retrospect
I began by tackling practice exams to identify the areas where I had knowledge gaps. To my surprise, I discovered that I was already well-versed in most of the topics. I spent a few weekends reviewing just to ensure I was fully prepared, and when the time came to take the exam, I breezed through it. Some of my weaker areas included Auto Scaling hooks, certain design patterns involving Route 53 resolver, and services like Elastic Beanstalk which I hadn't encountered frequently in practice. Overall, I'm thrilled to have earned the AWS SAP certification. It’s a gratifying reminder that the work I've done professionally and through side projects has taken me a long way when tested against industry standards.`,
        organization: "AWS",
        validationUrl: "https://aws.amazon.com/verification",
        validationCode: "0J5DY9KDGFBQ1L54",
        order: 1,
        rows: 2,
        cols: 2
    },
    {
        image: ckadWebp,
        pdf: ckadPdf,
        title: "Certified Kubernetes Application Developer",
        details: `### Technical Skills Learned/Refreshed
* Kubernetes Components
* Kubernetes Resource Types
* Kubernetes Custom Controller/Application Design for k8s
* Kubernetes Troubleshooting
* Kubernetes Management (kubectl, kubeadm)
* Helm
* Docker, Containerd
### Context/Purpose
I wanted to achieve the CKAD because after learning what I had on the job designing and implementing projects with k8s I wanted to formalize the knowledge I did obtain and then also learn the other resource types that I did not get to touch as much. 
### Post Certification Retrospect
I quite enjoyed studying for the CKAD as well as the exam itself. Due to its hands-on nature it was right up my alley. I'd learned quite a bit more about how k8s works and many resource types that I hadn't known about or gotten to play with. Things like CRDs and Custom Controllers for them, the design patterns and best practices of writing an application which utilizes the KubeAPI with authorization. I firmly believe that going forward the knowledge and practice I've gotten here will round me out in a great way in my career.`,
        organization: "Cloud Native Computing Foundation",
        shortOrg: "CNCF",
        validationUrl: "https://training.linuxfoundation.org/certification/verify/",
        validationCode: "LF-pppdxgaga1",
        order: 2,
        rows: 2,
        cols: 2
    },
    {
        image: awsDevopWebp,
        pdf: awsDevopPdf,
        title: "Certified DevOps Engineer - Professional",
        details: `### Technical Skills Learned/Refreshed
* AWS Code Suite (CodeBuild, CodeDeploy, CodeStar, CodeCommit, CodePipeline)
* General DevOps Deployment Methodlogies and Patterns
* 12 Factor App
* Resilience (Types of DR, and Resilience of the different AWS services)
* AWS-Centric Observability (Monitoring, Logging, Tracing)
### Context/Purpose
I achieved this certification during my tenure at Rackspace when I was on a "campaign" to formalize knowledge I'd learned on the job. I'd had several projects where the objective was to design and implement CI/CD pipelines for client software, some using AWS CodeSuite in part or in full and thus I wanted to educate myself on things I had not run into and solidify the knowledge with an industry certification.
### Post Certification Retrospect
In my opinion, the greatest "value add" I received from studying for the DevOps Professional certification was all that I'd learned about the resiliences/tolerances of failure for most of the common AWS services. Using the knowledge of resilience for each service I'm more capable of planning BC/DR in the realms of storage, compute, DBs and networking. I'd also spent the most time on those topics. 

While studying for the DevOps cert I also started learning about the 12-factor application model. Which I now follow as a framework when architecting pipelines for applications and applications themselves (including my own).

Overall the certification was worth it to me as it brought me deeper into the DevOps world.`,
        organization: "AWS",
        validationUrl: "https://aws.amazon.com/verification",
        validationCode: "4EHHM16DGFFQ1FCF",
        order: 3,
        rows: 2,
        cols: 2
    },
    {
        image: hctaWebp,
        pdf: hctaPdf,
        title: "Certified Terraform Associate",
        organization: "HashiCorp",
        validationUrl: "https://www.credly.com/badges/49f01784-69fd-4d30-a9c9-7f2dc766c3a4/public_url",
        order: 5,
        rows: 2,
        cols: 2
    },
    {
        image: hcvaWebp,
        pdf: hcvaPdf,
        title: "Certified Vault Associate",
        organization: "HashiCorp",
        validationUrl: "https://www.credly.com/badges/e4b9403a-00b5-42a2-a7a0-5244eee367e8/public_url",
        order: 6,
        rows: 2,
        cols: 2
    },
    {
        image: ufswdWebp,
        pdf: ufswdPdf,
        title: "Full Stack Web Developer",
        organization: "Udacity",
        validationUrl: "https://www.udacity.com/certificate/e/3dace34a-d37f-11e8-8531-a790145b8152",
        order: 7,
        rows: 2,
        cols: 2
    },
    {
        image: ccnarsWebp,
        pdf: ccnarsPdf,
        title: "CCNA Routing & Switching",
        organization: "Cisco",
        validationUrl: "https://cp.certmetrics.com/cisco/en/public/verify/credential",
        validationCode: "420158733462HNWG",
        order: 8,
        rows: 2,
        cols: 2
    },
    {
        image: caplusWebp,
        pdf: caplusPdf,
        title: "A+",
        details: `### Technical Skills Learned/Refreshed
* Computer Hardware
* Networking
* Security
* Troubleshooting
### Context/Purpose
At the start of my career, before I knew what domains actually existed in the world of professional technology I chose to obtain the A+. Truthfully, I'd only began looking into the A+ because of what I'd read about it in forums and what I'd heard about it from a professor at a community college I'd attended. Once I read more about it on the CompTIA official site I'd decided because the A+ touched briefly on most of the topics in the technological world it would be great for me to study for as it'd help me decide what domain I wanted to pursue. 
### Post Certification Retrospect
Earning the A+ was pivotal in my career in many different ways despite being a "beginner" certification. For one, the experience of studying for it and taking the exam laid for me a foundation and passion for auto-didacting(self-studying), learning new things, and gaining new tech skills. To this day I still utilize the fundamentals I'd learned in the A+/Net+/Sec+ to reverse engineer or troubleshoot issues in software, in the cloud, in networking, with electronics, and surprisingly enough in the automotive world. It opened my mind to things I had not been aware of outside of the consumer electronics world. Some things I had already known from my childhood years of tweaking with computers/laptops but *many* things I did not know. 

This certification was crucial in helping secure my first professional IT job as an IT specialist at CLand, and the entire experience changed my life.`,
        organization: "CompTIA",
        validationUrl: "https://www.certmetrics.com/comptia/public/verification.aspx/",
        validationCode: "DDF37GRBT3VEQZP4",
        order: 9,
        rows: 2,
        cols: 2
    },
    {
        image: canplusWebp,
        pdf: canplusPdf,
        title: "Network+",
        organization: "CompTIA",
        validationUrl: "https://www.certmetrics.com/comptia/public/verification.aspx/",
        validationCode: "NHXJSJJJYHE11N7Y",
        order: 10,
        rows: 2,
        cols: 2
    },
    {
        image: casplusWebp,
        pdf: casplusPdf,
        title: "Security+",
        organization: "CompTIA",
        validationUrl: "https://www.certmetrics.com/comptia/public/verification.aspx/",
        validationCode: "YEQSTDCMLCR12Z0L",
        order: 11,
        rows: 2,
        cols: 2
    },
    {
        image: awsSysopWebp,
        pdf: awsSysopPdf,
        title: "Certified SysOps Administrator - Associate",
        organization: "AWS",
        validationUrl: "https://aws.amazon.com/verification",
        validationCode: "8L4NWMGDBBB11NSM",
        order: 12,
        rows: 2,
        cols: 2
    },
    {
        image: mitxsixWebp,
        pdf: mitxsixPdf,
        title: "6.00x: Intro to Computer Science and Programming",
        organization: "MITx",
        validationUrl: "https://verify.edx.org/cert/f3e4b2ee4c394989b173bf379e99b457",
        order: 13,
        rows: 2,
        cols: 2
    },
    {
        image: itilWebp,
        pdf: itilPdf,
        title: "ITIL Foundation",
        organization: "Peoplecert",
        validationUrl: "https://www.peoplecert.org/for-corporations/certificate-verification-service",
        validationCode: "GR750136997KL",
        order: 14,
        rows: 2,
        cols: 2
    },
    {
        image: gadWebp,
        pdf: gadPdf,
        title: "Google Apps Certified Administrator",
        organization: "Google",
        order: 15,
        rows: 2,
        cols: 2
    },
];

export default CREDENTIALS;